import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Box } from '@chakra-ui/react';
import { SheetShapeDeepFragment, SheetScaleType } from '../../../gql/graphql';
import {
  LineSegment2,
  MultiPolygon2,
  Point2,
} from '../../../domain/geometry/geometric-types';
import {
  Viewport,
  getPointInPdfCoordinateSystem,
  getPointInViewerCoordinateSystem,
  SheetViewerContext,
} from '../../common/SheetViewer';
import { SheetPolygon } from './SheetPolygon';
import { SheetEdge } from './SheetEdge';
import { SheetVertex } from './SheetVertex';
import { SheetsViewerV2Context } from 'src/components/viewers/SheetsViewerV2/SheetsViewerV2.context';
import { useViewerMode, ViewerMode } from 'src/components/common/viewer-mode';
import { ApiCoordinatesConverter } from 'src/components/viewers/SheetsViewerV2/utils/apiCoordinatesConverter';
import { VIEWER_TOP_OFFSET } from 'src/components/viewers/SheetsViewerV2/SheetsViewerV2.config';
import { calculateScaleFactor } from 'src/hooks/useCopyPasteShapes';

export type PasteResult = {
  /** The position in PDF coordinates where shapes should be pasted */
  targetPosition: Point2;
  /** Scale factor to apply to shapes (preserves physical dimensions) */
  scaleFactor: number;
};

type PasteSheetShapesProps = {
  /** Shapes to paste (from clipboard) */
  shapes: SheetShapeDeepFragment[];
  /** Center point of the shapes (used as anchor) */
  sourceCenter: Point2;
  /** Source calibration for scale calculation */
  sourceCalibration: SheetScaleType;
  /** Callback when user clicks to paste */
  onResult: (result: PasteResult) => void;
  /** Callback to cancel paste mode */
  onCancel: () => void;
};

/**
 * Component for pasting sheet shapes with preview.
 * Shows intermediate shapes following the cursor, scaled based on calibration.
 * Click to confirm paste position.
 */
export const PasteSheetShapes = ({
  shapes,
  sourceCenter,
  sourceCalibration,
  onResult,
  onCancel,
}: PasteSheetShapesProps) => {
  const [cursorPoint, setCursorPoint] = useState<Point2 | null>(null);

  const { viewerMode } = useViewerMode();
  const sheetViewer = useContext(SheetViewerContext);
  const sheetViewerV2 = useContext(SheetsViewerV2Context);

  const isV2 = viewerMode === ViewerMode.SheetsV2 && sheetViewerV2;

  const viewerRef = isV2
    ? sheetViewerV2?.viewer.elementRef
    : sheetViewer?.viewerRef;
  const page = isV2
    ? sheetViewerV2?.activeSheet?.data?.page
    : sheetViewer?.page;
  const addViewportChangeHandler = isV2
    ? undefined
    : sheetViewer?.addViewportChangeHandler;
  const removeViewportChangeHandler = isV2
    ? undefined
    : sheetViewer?.removeViewportChangeHandler;

  const [viewerViewport, setViewerViewport] = useState<Viewport | null>(null);

  // Get target calibration from context
  const targetCalibration = sheetViewerV2?.calibration?.existing ?? null;

  // Calculate scale factor
  const scaleFactor = useMemo(() => {
    if (!targetCalibration) return 1;
    return calculateScaleFactor(sourceCalibration, targetCalibration);
  }, [sourceCalibration, targetCalibration]);

  // Helper function to get point from v2 viewer
  const getPointFromV2Viewer = useCallback(
    (clientX: number, clientY: number): Point2 | null => {
      if (
        !sheetViewerV2 ||
        !sheetViewerV2.viewer.aggregated ||
        !sheetViewerV2.activeSheet?.data
      ) {
        return null;
      }
      const viewer = sheetViewerV2.viewer.aggregated.viewer;
      const converter = new ApiCoordinatesConverter(
        viewer,
        sheetViewerV2.activeSheet.data.viewport
      );
      const result = viewer.impl.clientToWorld(
        clientX,
        clientY - VIEWER_TOP_OFFSET
      );
      if (result && result.point) {
        const api = converter.convertToApiPoint(result.point);
        return [api.x, api.y];
      }
      return null;
    },
    [sheetViewerV2]
  );

  // Helper function to get point from v1 viewer
  const getPointFromV1Viewer = useCallback(
    (clientX: number, clientY: number): Point2 | null => {
      if (!viewerViewport || !page || !viewerRef?.current) {
        return null;
      }
      // Type guard to ensure page is PageCallback
      if (typeof page === 'number') {
        return null;
      }
      return getPointInPdfCoordinateSystem(
        [clientX, clientY],
        viewerViewport.offset,
        viewerViewport.scale,
        page,
        viewerRef.current
      );
    },
    [viewerViewport, page, viewerRef]
  );

  // Get the point in the DOM coordinate system
  const getPointInDomCoordinateSystem = useCallback(
    (pointInPdf: Point2) => {
      if (isV2) {
        if (
          !sheetViewerV2 ||
          !sheetViewerV2.viewer.aggregated ||
          !sheetViewerV2.activeSheet?.data
        )
          throw new Error('Viewer not ready');

        const converter = new ApiCoordinatesConverter(
          sheetViewerV2.viewer.aggregated.viewer,
          sheetViewerV2.activeSheet.data.viewport
        );
        const world = converter.convertToAutodeskPoint({
          x: pointInPdf[0],
          y: pointInPdf[1],
        });
        const screen =
          sheetViewerV2.viewer.aggregated.viewer.worldToClient(world);
        return [screen.x, screen.y] as Point2;
      }

      if (!viewerViewport || !page || !viewerRef?.current) {
        throw new Error('Viewer not ready');
      }
      // Type guard to ensure page is PageCallback
      if (typeof page === 'number') {
        throw new Error('Invalid page type');
      }
      return getPointInViewerCoordinateSystem(
        pointInPdf,
        viewerViewport.offset,
        viewerViewport.scale,
        page
      );
    },
    [isV2, sheetViewerV2, viewerViewport, page, viewerRef]
  );

  useEffect(() => {
    if (isV2 || !addViewportChangeHandler || !removeViewportChangeHandler)
      return;
    const onViewportChange = (vp: Viewport) => {
      setViewerViewport(vp);
    };
    addViewportChangeHandler(onViewportChange);
    return () => {
      removeViewportChangeHandler(onViewportChange);
    };
  }, [isV2, addViewportChangeHandler, removeViewportChangeHandler]);

  // Handle click to paste
  const handleClick = useCallback(
    (event: MouseEvent) => {
      if (event.button !== 0) return;

      let targetPosition: Point2 | null = null;

      if (isV2) {
        targetPosition = getPointFromV2Viewer(event.clientX, event.clientY);
      } else {
        targetPosition = getPointFromV1Viewer(event.clientX, event.clientY);
      }

      if (!targetPosition) {
        return;
      }

      onResult({
        targetPosition,
        scaleFactor,
      });
    },
    [isV2, getPointFromV2Viewer, getPointFromV1Viewer, onResult, scaleFactor]
  );

  useEffect(() => {
    const currentViewer = viewerRef?.current;
    if (!currentViewer) {
      return;
    }

    currentViewer.addEventListener('click', handleClick);
    return () => {
      currentViewer.removeEventListener('click', handleClick);
    };
  }, [handleClick, viewerRef]);

  // Handle pointer move to update preview
  const handlePointerMove = useCallback(
    (event: PointerEvent) => {
      let newPoint: Point2 | null = null;

      if (isV2) {
        newPoint = getPointFromV2Viewer(event.clientX, event.clientY);
      } else {
        newPoint = getPointFromV1Viewer(event.clientX, event.clientY);
      }

      if (!newPoint) {
        return;
      }
      setCursorPoint(newPoint);
    },
    [isV2, getPointFromV2Viewer, getPointFromV1Viewer]
  );

  useEffect(() => {
    const current = viewerRef?.current;
    if (!current) {
      return;
    }
    current.addEventListener('pointermove', handlePointerMove);
    return () => {
      current.removeEventListener('pointermove', handlePointerMove);
    };
  }, [handlePointerMove, viewerRef]);

  // Handle right-click to cancel
  useEffect(() => {
    const handleContextMenu = (event: MouseEvent) => {
      event.preventDefault();
      onCancel();
    };

    const currentViewer = viewerRef?.current;
    if (!currentViewer) return;

    currentViewer.addEventListener('contextmenu', handleContextMenu);
    return () => {
      currentViewer.removeEventListener('contextmenu', handleContextMenu);
    };
  }, [onCancel, viewerRef]);

  // Transform a point from source to target position with scaling
  const transformPoint = useCallback(
    (point: Point2, targetPosition: Point2): Point2 => {
      const relX = point[0] - sourceCenter[0];
      const relY = point[1] - sourceCenter[1];
      return [
        targetPosition[0] + relX * scaleFactor,
        targetPosition[1] + relY * scaleFactor,
      ];
    },
    [sourceCenter, scaleFactor]
  );

  const previewShapes = useMemo(() => {
    if (!cursorPoint) return null;

    return (
      <Box
        width={'100%'}
        height={'100%'}
        position={'absolute'}
        top={0}
        left={0}
        zIndex={1000}
        pointerEvents={'none'}
      >
        {shapes.map((shape) => {
          if ('sheetShapePolygon' in shape && shape.sheetShapePolygon) {
            return (
              <IntermediatePolygonShape
                key={shape.id}
                shape={shape}
                targetPosition={cursorPoint}
                transformPoint={transformPoint}
                getPointInDomCoordinateSystem={getPointInDomCoordinateSystem}
              />
            );
          } else if ('sheetShapeLine' in shape && shape.sheetShapeLine) {
            return (
              <IntermediateLineShape
                key={shape.id}
                shape={shape}
                targetPosition={cursorPoint}
                transformPoint={transformPoint}
                getPointInDomCoordinateSystem={getPointInDomCoordinateSystem}
              />
            );
          } else if ('sheetShapePoint' in shape && shape.sheetShapePoint) {
            return (
              <IntermediatePointShape
                key={shape.id}
                shape={shape}
                targetPosition={cursorPoint}
                transformPoint={transformPoint}
                getPointInDomCoordinateSystem={getPointInDomCoordinateSystem}
              />
            );
          } else if ('sheetShapeLegend' in shape && shape.sheetShapeLegend) {
            return (
              <IntermediateLegendShape
                key={shape.id}
                shape={shape}
                targetPosition={cursorPoint}
                transformPoint={transformPoint}
                getPointInDomCoordinateSystem={getPointInDomCoordinateSystem}
                scaleFactor={scaleFactor}
              />
            );
          }
          return null;
        })}
      </Box>
    );
  }, [
    cursorPoint,
    shapes,
    transformPoint,
    getPointInDomCoordinateSystem,
    scaleFactor,
  ]);

  return <>{previewShapes}</>;
};

type IntermediateShapeProps = {
  shape: SheetShapeDeepFragment;
  targetPosition: Point2;
  transformPoint: (point: Point2, targetPosition: Point2) => Point2;
  getPointInDomCoordinateSystem: (pointInPdf: Point2) => Point2;
};

const IntermediatePolygonShape = ({
  shape,
  targetPosition,
  transformPoint,
  getPointInDomCoordinateSystem,
}: IntermediateShapeProps) => {
  if (!shape.sheetShapePolygon?.multipolygon) return null;

  const transformedMultipolygon: MultiPolygon2 = {
    polygons: shape.sheetShapePolygon.multipolygon.polygons.map((polygon) => ({
      exterior: polygon.exterior.points.map((p) =>
        transformPoint([p.x, p.y], targetPosition)
      ),
      interiors: polygon.interiors.map((interior) =>
        interior.points.map((p) => transformPoint([p.x, p.y], targetPosition))
      ),
    })),
  };

  return (
    <SheetPolygon
      multipolygon={transformedMultipolygon}
      getPointInDomCoordinateSystem={getPointInDomCoordinateSystem}
      fill={'orange.300'}
    />
  );
};

const IntermediateLineShape = ({
  shape,
  targetPosition,
  transformPoint,
  getPointInDomCoordinateSystem,
}: IntermediateShapeProps) => {
  if (!shape.sheetShapeLine?.points) return null;

  const transformedPoints = shape.sheetShapeLine.points.map((p) =>
    transformPoint([p.x, p.y], targetPosition)
  );

  const edges: LineSegment2[] = [];
  for (let i = 0; i < transformedPoints.length - 1; i++) {
    edges.push([transformedPoints[i], transformedPoints[i + 1]]);
  }

  return (
    <>
      {edges.map((edge, index) => (
        <SheetEdge
          key={`${shape.id}-edge-${index}`}
          segment={edge}
          cursor={'pointer'}
          getPointInDomCoordinateSystem={getPointInDomCoordinateSystem}
          backgroundColor={'orange.300'}
        />
      ))}
    </>
  );
};

const IntermediatePointShape = ({
  shape,
  targetPosition,
  transformPoint,
  getPointInDomCoordinateSystem,
}: IntermediateShapeProps) => {
  if (!shape.sheetShapePoint) return null;

  const transformedPoint = transformPoint(
    [shape.sheetShapePoint.point.x, shape.sheetShapePoint.point.y],
    targetPosition
  );

  return (
    <SheetVertex
      point={transformedPoint}
      getPointInDomCoordinateSystem={getPointInDomCoordinateSystem}
      theme={'default'}
      backgroundColor={'orange.300'}
      borderColor={'orange.300'}
    />
  );
};

// Legend shape constants for intermediate preview
// These match the viewer coordinate constants in LegendShape.tsx,
// scaled to PDF/API coordinates (scale factor ~71.4 based on ROW_HEIGHT)
const LEGEND_SCALE_FACTOR = 71.4;
const LEGEND_ROW_HEIGHT_SCALED = 0.35 * LEGEND_SCALE_FACTOR;
const LEGEND_PADDING_SCALED = 0.12 * LEGEND_SCALE_FACTOR;
const LEGEND_MIN_WIDTH_SCALED = 2.0 * LEGEND_SCALE_FACTOR;
const LEGEND_COLOR_SWATCH_SIZE_SCALED = 0.2 * LEGEND_SCALE_FACTOR;
const LEGEND_TEXT_HEIGHT_SCALED = 0.18 * LEGEND_SCALE_FACTOR;
const LEGEND_TEXT_PADDING_SCALED = 0.08 * LEGEND_SCALE_FACTOR;

type IntermediateLegendShapeProps = IntermediateShapeProps & {
  scaleFactor: number;
};

const IntermediateLegendShape = ({
  shape,
  targetPosition,
  transformPoint,
  getPointInDomCoordinateSystem,
  scaleFactor,
}: IntermediateLegendShapeProps) => {
  if (!shape.sheetShapeLegend) return null;

  const transformedPosition = transformPoint(
    [shape.sheetShapeLegend.position.x, shape.sheetShapeLegend.position.y],
    targetPosition
  );

  const entries = shape.sheetShapeLegend.legendOrder?.orderEntries || [];
  const entriesCount = entries.length || 1;

  // Calculate height based on entries (scaled by scaleFactor)
  const legendHeight =
    (entriesCount * LEGEND_ROW_HEIGHT_SCALED + LEGEND_PADDING_SCALED * 2) *
    scaleFactor;

  // Calculate width based on longest entry name
  const charWidth = LEGEND_TEXT_HEIGHT_SCALED * 0.5;
  const maxNameLength = Math.max(
    ...entries.map((e) => e.name?.length || 0),
    10
  );
  const textWidth = maxNameLength * charWidth;

  const legendWidth =
    Math.max(
      LEGEND_MIN_WIDTH_SCALED,
      LEGEND_PADDING_SCALED +
        LEGEND_COLOR_SWATCH_SIZE_SCALED +
        LEGEND_TEXT_PADDING_SCALED +
        textWidth +
        LEGEND_PADDING_SCALED
    ) * scaleFactor;

  // Create a polygon representing the legend bounds
  const topLeft: Point2 = [transformedPosition[0], transformedPosition[1]];
  const topRight: Point2 = [
    transformedPosition[0] + legendWidth,
    transformedPosition[1],
  ];
  const bottomRight: Point2 = [
    transformedPosition[0] + legendWidth,
    transformedPosition[1] - legendHeight,
  ];
  const bottomLeft: Point2 = [
    transformedPosition[0],
    transformedPosition[1] - legendHeight,
  ];

  const multipolygon: MultiPolygon2 = {
    polygons: [
      {
        exterior: [topLeft, topRight, bottomRight, bottomLeft, topLeft],
        interiors: [],
      },
    ],
  };

  return (
    <SheetPolygon
      multipolygon={multipolygon}
      getPointInDomCoordinateSystem={getPointInDomCoordinateSystem}
      fill={'orange.300'}
    />
  );
};
